import {
  Bell,
  Eye,
  KeyRound,
  LifeBuoy,
  LogOut,
  Paperclip,
  MessageSquareText,
  Plug,
  PlugZap,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  Star,
  Timer,
  Ticket,
  type LucideIcon,
} from "lucide-react";
import { Permission, type PermissionName } from "@/components/session-provider";

/**
 * The settings navigation, as data.
 *
 * A single declarative list rather than JSX scattered across the page, because three
 * things have to agree and drift between them is invisible:
 *
 *   - what the sidebar shows
 *   - what search matches
 *   - what the user is allowed to see
 *
 * Deriving all three from one array means a section cannot appear in search but be
 * missing from the nav, and cannot be reachable by URL while hidden from both.
 *
 * `requires` is a RENDERING filter. Every endpoint behind these screens re-checks
 * server-side; hiding a section here is courtesy, not a control.
 */

export interface SettingsSection {
  id: string;
  label: string;
  description: string;
  icon: LucideIcon;
  requires?: PermissionName;
  /**
   * Extra words search should match. Users look for "email" long before they look for
   * "Azure Communication Services", and a settings search that only matches the label is
   * a settings search nobody uses twice.
   */
  keywords: string[];
}

export interface SettingsGroup {
  id: string;
  label: string;
  sections: SettingsSection[];
}

export const settingsGroups: SettingsGroup[] = [
  {
    id: "features",
    label: "Features",
    sections: [
      {
        id: "features-ticketing",
        label: "Ticketing",
        description: "Attachments, reopening, on-hold, and bulk export.",
        icon: Ticket,
        requires: Permission.SettingsRead,
        keywords: [
          "attachment",
          "file",
          "upload",
          "reopen",
          "hold",
          "pause",
          "sla",
          "export",
          "csv",
          "download",
          "bulk",
        ],
      },
      {
        id: "features-assistance",
        label: "Assistance",
        description: "AI ticket assistant and suggestions.",
        icon: LifeBuoy,
        requires: Permission.SettingsRead,
        keywords: [
          "ai",
          "assistant",
          "openai",
          "azure openai",
          "suggest",
          "summarise",
          "summarize",
        ],
      },
      {
        id: "features-notifications",
        label: "Notifications",
        description: "Email, SMS, and voice notifications.",
        icon: Bell,
        requires: Permission.SettingsRead,
        keywords: ["email", "mail", "sms", "text", "voice", "call", "notify", "alert", "acs"],
      },
      {
        id: "features-directory",
        label: "Directory",
        description: "Reconciling users with Entra.",
        icon: RefreshCw,
        requires: Permission.SettingsRead,
        keywords: ["directory", "sync", "entra", "azure ad", "graph", "users", "leaver"],
      },
      {
        id: "features-access",
        label: "Access",
        description: "Break-glass and sign-in help.",
        icon: KeyRound,
        requires: Permission.SettingsRead,
        keywords: [
          "break glass",
          "breakglass",
          "emergency",
          "sign in",
          "login",
          "help",
          "password",
        ],
      },
    ],
  },
  {
    id: "connections",
    label: "Connections",
    sections: [
      {
        id: "integrations",
        label: "Integrations",
        description: "Azure Communication Services, RingCentral, ElevenLabs.",
        icon: Plug,
        requires: Permission.IntegrationManage,
        keywords: [
          "integration",
          "acs",
          "azure communication",
          "email",
          "smtp",
          "sender",
          "ringcentral",
          "ring central",
          "sms",
          "phone",
          "voice",
          "elevenlabs",
          "eleven labs",
          "speech",
          "tts",
          "text to speech",
          "api key",
          "secret",
          "key vault",
          "credential",
          "connect",
        ],
      },
      {
        // ⚠️ Its own section rather than a tab inside Integrations, and the split is the
        // point: that one is systems WE TALK TO, this is systems that TALK TO US. Opposite
        // trust directions and opposite failure modes, and listing them together invites
        // somebody to reason about one using the other's rules (D-120).
        id: "connected-apps",
        label: "Connected apps",
        description: "Applications allowed to raise requests over the API.",
        icon: PlugZap,
        requires: Permission.IntegrationManage,
        keywords: [
          "api",
          "connected app",
          "application",
          "integration key",
          "api key",
          "token",
          "railway",
          "machine",
          "service account",
          "inbound",
          "create ticket",
          "raise request",
          "webhook",
          "automation",
          "monitoring",
        ],
      },
      {
        id: "sla",
        label: "Response targets",
        description: "How fast each priority is answered and resolved, and when the desk is open.",
        icon: Timer,
        requires: Permission.SettingsManage,
        keywords: [
          "sla",
          "service level",
          "target",
          "deadline",
          "due",
          "breach",
          "overdue",
          "response time",
          "first response",
          "resolution",
          "priority",
          "business hours",
          "working hours",
          "opening hours",
          "clock",
          "24/7",
          "timezone",
          "escalation",
        ],
      },
      {
        id: "saved-replies",
        label: "Saved replies",
        description: "Predefined replies agents can drop into a ticket and edit.",
        icon: MessageSquareText,
        requires: Permission.SettingsManage,
        keywords: [
          "canned",
          "response",
          "reply",
          "template",
          "macro",
          "snippet",
          "predefined",
          "quick reply",
          "boilerplate",
          "phrase",
        ],
      },
      {
        id: "sign-out",
        label: "Sign-out page",
        description: "Where people land after signing out.",
        icon: LogOut,
        requires: Permission.SettingsRead,
        keywords: [
          "sign out",
          "signout",
          "logout",
          "log out",
          "redirect",
          "landing",
          "sso",
          "session",
        ],
      },
      {
        id: "external-domains",
        label: "External domains",
        description:
          "Organisations outside the compliance boundary. Their people get notifications with no ticket content.",
        icon: ShieldAlert,
        requires: Permission.IntegrationManage,
        keywords: [
          "external",
          "partner",
          "vendor",
          "contractor",
          "third party",
          "domain",
          "boundary",
          "compliance",
          "baa",
          "guest",
          "b2b",
          "notification",
          "disclosure",
          "redact",
        ],
      },
      {
        id: "notification-templates",
        label: "Email templates",
        description:
          "The HTML each notification is built from. Placeholders only ever resolve to what the recipient may already see.",
        icon: MessageSquareText,
        requires: Permission.NotificationTemplateManage,
        keywords: [
          "template",
          "email",
          "html",
          "notification",
          "branding",
          "placeholder",
          "variable",
          "merge",
        ],
      },
      {
        id: "attachment-formats",
        label: "Accepted file types",
        description:
          "Which file formats the desk accepts. Every file is identified by its contents, never by its name.",
        icon: Paperclip,
        requires: Permission.SettingsRead,
        keywords: [
          "attachment",
          "file",
          "format",
          "type",
          "extension",
          "upload",
          "msg",
          "outlook",
          "email",
          "zip",
          "archive",
          "pdf",
          "image",
          "allow",
          "block",
        ],
      },
      {
        id: "vip-titles",
        label: "VIP titles",
        description:
          "Job titles that mark their holder VIP. Routing and presentation only — VIP grants no extra access.",
        icon: Star,
        requires: Permission.SettingsManage,
        keywords: [
          "vip",
          "important",
          "executive",
          "director",
          "vp",
          "chief",
          "ceo",
          "title",
          "priority",
          "escalation",
        ],
      },
      {
        id: "ai-transcripts",
        label: "Assistant transcripts",
        description: "Chat assistant conversations, read for evaluating it for misuse.",
        icon: Eye,
        requires: Permission.AiTranscriptsRead,
        keywords: [
          "ai",
          "assistant",
          "tars",
          "transcript",
          "conversation",
          "chat",
          "misuse",
          "abuse",
          "review",
        ],
      },
      {
        id: "security",
        label: "Security posture",
        description: "What is enforced, and what is not. Read-only.",
        icon: ShieldCheck,
        requires: Permission.SettingsRead,
        keywords: [
          "security",
          "hipaa",
          "encryption",
          "tde",
          "rls",
          "row level",
          "audit",
          "ledger",
          "csp",
          "hsts",
          "headers",
          "compliance",
        ],
      },
    ],
  },
];

/** Flat list, for search and for resolving a section id from the URL. */
export const allSections: SettingsSection[] = settingsGroups.flatMap((g) => g.sections);

/**
 * Substring match across label, description and keywords.
 *
 * Deliberately simple. Fuzzy matching on a settings list of this size mostly produces
 * confident wrong answers — someone typing "sms" wants the two sections that mention SMS,
 * not the five that score above a threshold.
 */
export function searchSections(query: string, sections: SettingsSection[]): SettingsSection[] {
  const q = query.trim().toLowerCase();
  if (!q) return sections;

  return sections.filter((section) => {
    const haystack = [section.label, section.description, ...section.keywords]
      .join(" ")
      .toLowerCase();

    return haystack.includes(q);
  });
}
